import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Minimum substring cover problem",
  "description": "",
  "published": "2012-01-11T00:00:00.000Z",
  "redirect_from": ["/blog/minimum-substring-cover-problem/swizec/3406"],
  "categories": "Algorithm, Approximation algorithm, Combinatorics, Linear programming, Math, NP-hard, Substring, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`A major part of `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/Le-Thesis"
      }}>{`my thesis`}</a>{`involves finding an algorithm to discover a good substring cover of text in order to properly syllabify said text. But what is the substring cover problem anyway and what does it entail?`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/01/11259239_fe6b60af02_m5.jpg",
        "alt": "algorithms doodle",
        "title": "algorithms doodle"
      }}></img></p>
    <p><a parentName="p" {...{
        "href": "http://cs.haifa.ac.il/~dhermeli/Publications/WAOA2007.pdf"
      }}>{`The Minimum Substring Cover Problem`}</a>{` paper from Hermelin, Rawitz, Rizzi and Vialette dating back to 2007 (judging by the filename) serves as a good entry point into this topic.`}</p>
    <p>{`There are actually a lot of cover problems, the most famous being Minimum `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Set_cover_problem",
        "title": "Set cover problem"
      }}>{`Set Cover`}</a>{` and `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Vertex_cover",
        "title": "Vertex cover"
      }}>{`Minimum Vertex Cover`}</a>{` problems. In this type of problems we are faced with two sets of elements and we want to cover one of the sets with another, by using the "least" elements from the covering set. I put "least" in quotes because the definition depends on what we want - maybe we want to use the least number of elements, perhaps we want the shortest elements ... whatever.`}</p>
    <p>{`For an example consider this:`}</p>
    <p>{`S = `}{`['a', 'aab', 'aba']`}{`
C(S) = `}{`['a', 'b', 'aa', 'ab', 'ba', 'aab', 'aba']`}</p>
    <p>{`We can easily see that `}<em parentName="p">{`C(S)`}</em>{` is a set of all the possible coverings of S - using a combination of strings from C we can construct every string in S. This part isn't very difficult to calculate.`}</p>
    <p>{`Everything gets slightly hairier when you look for minimum covers:`}</p>
    <p>{`C_1 = `}{`['a', 'b']`}{` # 3-cover (need 3 strings to cover the longest string in S)
C_2 = `}{`['a', 'ab']`}{` # 2-cover (need 2 strings to cover the longest string in S)`}</p>
    <p>{`Depending on how you choose the weight, both C`}<em parentName="p">{`1 and C_2 are `}{`_`}{`minimum substring covers`}</em>{` of S. Considering "least" to mean `}<em parentName="p">{`least amount of strings`}</em>{` then both are of weight 2, but if you consider "least" to mean `}<em parentName="p">{`the total length of strings`}</em>{` then C_1 is better.`}</p>
    <p>{`You could easily argue C_2 is better, because it uses the least amount of elements to cover the whole set S. 1+3+3 = 7 for C_1 and 1+2+2 = 5 for C_2.`}</p>
    <p>{`Ok, so now we know that finding the minimum substring cover of a set of strings depends a whole lot on what you actually want. Always a good sign, having a well-known problem where people can't even agree on what the best solution looks like.`}</p>
    <p>{`The paper goes on to explain in great theoretical detail that, because this problem is similar to minimum vertex cover, minimum set cover and similar problems, it is `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/NP-hard",
        "title": "NP-hard"
      }}>{`NP-hard`}</a>{` to approximate. This means that the problem is at least as hard as the hardest problems in NP, but it doesn't necessarily mean that there is no polynomial solution - it just hasn't been found yet.`}</p>
    <p>{`Luckily, if we constrain some parameters of the problem, it becomes/remains `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/APX",
        "title": "APX"
      }}>{`APX-hard`}</a>{` - `}<em parentName="p">{`problems in this class have efficient algorithms that can find an answer within some fixed percentage of the optimal answer.`}</em></p>
    <p>{`The article then proposes two `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Approximation_algorithm",
        "title": "Approximation algorithm"
      }}>{`approximation algorithms`}</a>{` for finding minimum substring covers of S.`}</p>
    <h2 {...{
      "id": "local-ratio-algorithms"
    }}>{`Local-Ratio Algorithms`}</h2>
    <p>{`This algorithm follows from the local-ratio lemma, which in the case of substring cover means`}</p>
    <blockquote>
      <p parentName="blockquote">{`Let C be a cover for S, and let w_1 and w_2 be weight functions for C(S). If C is an alpha-approximate, both with respect to w_1 and with respect to w_2, then C is also alpha-approximate with respect to w_1+w_2.`}</p>
    </blockquote>
    <pre><code parentName="pre" {...{
        "className": "language-pseudocode"
      }}>{`Data: A set of strings S, a weight function w:C(S) -> Q+, an integer l >= 2
Result: An l-cover C for S (l is the number of substrings covering the longest s in S)
begin

C
`}</code></pre>
    <p>{`This algorithm is guaranteed to terminate after a polynomial amount of recursive calls and it returns a (((m+1) binomial 2) - 1)-approximate l-cover of S.`}</p>
    <p>{`In sensible terms the algorithm basically does this: Add everything with zero weight to a partial solution, if this isn't the solution, it selects an uncovered substring in S and tries to cover it by examining all substrings in C_s.`}</p>
    <h2 {...{
      "id": "linear-programming-rounding"
    }}><a parentName="h2" {...{
        "href": "http://en.wikipedia.org/wiki/Linear_programming",
        "title": "Linear programming"
      }}>{`Linear Programming`}</a>{` Rounding`}</h2>
    <p>{`Originally the linear programming rounding algorithm was developed by Hajiaghayi et all. for the Minimum Multicolored Subgraph problem when l=2. It has now been expanded for any constant value of l.`}</p>
    <p>{`This section is extremely light on practical results and just shows a bunch of mathematics that supposedly prove how the algorithm can be extended and that the final result is an O(log^(1/l) n `}{`*`}{` m^((l/1)^2/l))-approximate algorithm.`}</p>
    <p>{`From what I can understand this algorithm approaches the problem with the idea that they are basically looking for l-factorizations of strings.`}</p>
    <p>{`According to this section, the minimum substring cover can be formulated using the following integer linear program:`}</p>
    <p>{`min SUM`}<em parentName="p">{`(c in C(s)) w(c)x_c
s.t. SUM`}</em>{`(f in F`}<em parentName="p">{`l(s)) y_f >= 1 every s in S
SUM`}</em>{`(c in f in F_l(s)) y_f`}</p>
    <p>{`Then there are a bunch of proofs that this algorithm works and is indeed very awesome ... but by this time my eyes started glazing over and the September deadline for my thesis started looking `}<em parentName="p">{`very`}</em>{` near.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/7467186/apply-substring-on-values-in-linq"
        }}>{`Apply substring on values in LINQ`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/8046107/objective-c-substring-range-exception"
        }}>{`Objective-c Substring Range Exception`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/7904270/substr-and-explode-in-javascript"
        }}>{`Substr and explode in JavaScript`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://spokutta.wordpress.com/2012/01/05/1311/"
        }}>{`On linear programming formulations for the TSP polytope`}</a>{` (spokutta.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://ntz-develop.blogspot.com/2011/03/fuzzy-string-search.html"
        }}>{`Fuzzy string search`}</a>{` (ntz-develop.blogspot.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://11011110.livejournal.com/238766.html"
        }}>{`Top ten algorithms preprints of 2011`}</a>{` (11011110.livejournal.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/8456010/efficiently-replace-a-fixed-position-substring-with-a-string-of-equal-or-larger"
        }}>{`Efficiently replace a fixed position substring with a string of equal or larger length`}</a>{` (stackoverflow.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=a0995159-d508-465f-9dd9-b53632d16b06",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      